import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchInput } from "@/components/common/SearchInput";
import { Wallet, Settings, PlayCircle, FileCog } from "lucide-react";
import { toast } from "sonner";

const PAYROLL_STATUSES = ["Draft", "Submitted", "Approved", "Paid"];

const NEXT_STATUS = { Draft: "Submitted", Submitted: "Approved", Approved: "Paid" };
const NEXT_LABEL = { Draft: "Submit for Approval", Submitted: "Approve", Approved: "Mark Paid" };

function currentMonth() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
}

export default function Payroll() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [month, setMonth] = useState(currentMonth());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: rows, isLoading } = useQuery({ queryKey: ["payroll", month], queryFn: () => get(`/hrms/payroll?month=${month}`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (search && !umap[r.employee_id]?.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return true;
    });
  }, [rows, umap, search, statusFilter]);

  const generate = useMutation({
    mutationFn: () => post("/hrms/payroll/generate", { month }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["payroll", month] }); toast.success(`Payroll generated for ${res.length} employees`); },
  });
  const advance = useMutation({
    mutationFn: ({ id, status }) => patch(`/hrms/payroll/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payroll", month] }); toast.success("Status updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update"),
  });

  if (isLoading) return <Skeleton className="h-64" />;
  const totals = filtered.reduce((a, r) => ({
    gross: a.gross + r.gross, lop: a.lop + r.lop_deduction, reimb: a.reimb + r.reimbursement_total, net: a.net + r.net,
  }), { gross: 0, lop: 0, reimb: 0, net: 0 });

  return (
    <div>
      <PageHeader title="Payroll Run" subtitle={month}>
        <input type="month" data-testid="payroll-month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" />
        {can("hrms", "edit") && (
          <Button variant="outline" data-testid="run-payroll-btn" onClick={() => generate.mutate()} disabled={generate.isPending}>
            <PlayCircle className="mr-1.5 h-4 w-4" />Run Payroll
          </Button>
        )}
        {can("hrms", "override") && (
          <Button variant="outline" data-testid="payroll-config-link" onClick={() => navigate("/hrms/payroll/config")}>
            <Settings className="mr-1.5 h-4 w-4" />Config
          </Button>
        )}
      </PageHeader>

      {!rows?.length ? (
        <EmptyState icon={Wallet} title="No payroll generated for this month" description="Run payroll to compute pay for every active employee." />
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <SearchInput testid="payroll-search" placeholder="Search employee" value={search} onChange={setSearch} />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="payroll-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {PAYROLL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={Wallet} title="No matching payroll records" description="Adjust your search or filter." />
          ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Employee</th><th className="px-4 py-3 text-right font-semibold">Gross</th>
              <th className="px-4 py-3 text-right font-semibold">LOP</th><th className="px-4 py-3 text-right font-semibold">Reimbursement</th>
              <th className="px-4 py-3 text-right font-semibold">Net Pay</th><th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`payroll-row-${r.employee_id}`} className="border-b border-border hover:bg-muted/50">
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/hrms/directory/${r.employee_id}`)}>
                    <div className="flex items-center gap-2"><Avatar user={umap[r.employee_id]} size="sm" /><span className="font-medium">{umap[r.employee_id]?.name}</span></div>
                  </td>
                  <td className="px-4 py-3 text-right">{money(r.gross)}</td>
                  <td className="px-4 py-3 text-right text-red-500">{r.lop_deduction ? `- ${money(r.lop_deduction)}` : "—"}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{r.reimbursement_total ? `+ ${money(r.reimbursement_total)}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-bold">{money(r.net)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {can("hrms", "edit") && (
                        <Button size="sm" variant="outline" data-testid={`generate-payslip-${r.id}`}
                          onClick={() => navigate(`/hrms/documents/generate?entityType=employee&entityId=${r.employee_id}&payrollId=${r.id}&returnTo=${encodeURIComponent(`/hrms/directory/${r.employee_id}`)}`)}>
                          <FileCog className="mr-1.5 h-3.5 w-3.5" />Generate
                        </Button>
                      )}
                      {can("hrms", "approve") && NEXT_STATUS[r.status] && (
                        <Button size="sm" variant="outline" data-testid={`advance-payroll-${r.id}`}
                          onClick={() => advance.mutate({ id: r.id, status: NEXT_STATUS[r.status] })}>
                          {NEXT_LABEL[r.status]}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="bg-muted/40 font-bold">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right">{money(totals.gross)}</td>
                <td className="px-4 py-3 text-right text-red-500">- {money(totals.lop)}</td>
                <td className="px-4 py-3 text-right text-emerald-600">+ {money(totals.reimb)}</td>
                <td className="px-4 py-3 text-right">{money(totals.net)}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
          )}
        </>
      )}
    </div>
  );
}
